import Head from "next/head";
import React from "react";

import type { Route } from "nextjs-routes";
import type { Props as PageProps } from "nextjs/getServerSideProps/handlers";

import config from "configs/app";
import * as metadata from "lib/metadata";
import { SITE_NAME, DEFAULT_OG_IMAGE, baseMetadata } from "lib/seoConfig";

// 不需要被搜索引擎索引的路由
const NOINDEX_ROUTES: Array<Route["pathname"]> = [
  "/auth/profile",
  "/account/merits",
  "/account/watchlist",
  "/account/api-key",
  "/account/custom-abi",
  "/account/tag-address",
  "/account/verified-addresses",
  "/login",
  "/404",
];

interface Props<Pathname extends Route["pathname"]> {
  pathname: Pathname;
  query?: PageProps<Pathname>["query"];
  apiData?: PageProps<Pathname>["apiData"];
}

const PageMetadata = <Pathname extends Route["pathname"]>(
  props: Props<Pathname>,
) => {
  const { title, description, keywords, opengraph, canonical } =
    metadata.generate(props, props.apiData);

  // 判断是否需要 noindex
  const shouldNoIndex = NOINDEX_ROUTES.includes(props.pathname);

  // 确保 OG 图片使用绝对路径
  const ogImageUrl = opengraph.imageUrl?.startsWith("http")
    ? opengraph.imageUrl
    : DEFAULT_OG_IMAGE;

  return (
    <Head>
      {/* 基础 SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content={(keywords ?? baseMetadata.keywords) as string}
      />

      {/* Robots 指令 */}
      {shouldNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Canonical 和多语言 */}
      {canonical && <link rel="canonical" href={canonical} />}
      {canonical && <link rel="alternate" hrefLang="en" href={canonical} />}
      {canonical && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      {/* Open Graph 标签 */}
      <meta property="og:title" content={opengraph.title} />
      <meta
        property="og:description"
        content={opengraph.description || description}
      />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={opengraph.title} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={opengraph.siteName || SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter Card 标签 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content={config.app.host} />
      <meta name="twitter:title" content={opengraph.title} />
      <meta
        name="twitter:description"
        content={opengraph.description || description}
      />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={opengraph.title} />
      <meta
        name="twitter:site"
        content={config.meta.twitter?.site || "@MorphNetwork"}
      />
      <meta
        name="twitter:creator"
        content={config.meta.twitter?.creator || "@MorphNetwork"}
      />

      {/* 移动端优化 */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
      <meta name="format-detection" content="telephone=no" />

      {/* 主题颜色 */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
    </Head>
  );
};

export default PageMetadata;
